import type { QuaternionSignalModalityV1, SignalNativeSeriesV1, SignalWindowV1 } from "@rekonas/domain";

export type QuaternionWxyz = readonly [number, number, number, number];
export type Vector3 = readonly [number, number, number];

export interface TiltOnlyOrientation {
  quaternion: QuaternionWxyz;
  inclinationDegrees: number;
  singular: boolean;
}

export type OrientationSelection =
  | { state: "available"; sensor: QuaternionWxyz; model: QuaternionWxyz; sampleTime: number }
  | { state: "unavailable"; reason: "out_of_coverage" | "gap" | "invalid" | "mapping" | "loading" | "suspended" | "access_unvalidated" | "sensor_absent" };

const SQRT_HALF = Math.SQRT1_2;
export const RX_NEGATIVE_90: QuaternionWxyz = [SQRT_HALF, -SQRT_HALF, 0, 0];

export function normalizeQuaternion(value: QuaternionWxyz): QuaternionWxyz | null {
  if (value.some((component) => !Number.isFinite(component))) return null;
  const norm = Math.hypot(...value);
  if (norm < 1e-6 || norm < 0.9 || norm > 1.1) return null;
  return value.map((component) => component / norm) as unknown as QuaternionWxyz;
}

export function multiplyQuaternion(left: QuaternionWxyz, right: QuaternionWxyz): QuaternionWxyz {
  const [aw, ax, ay, az] = left;
  const [bw, bx, by, bz] = right;
  return [
    aw * bw - ax * bx - ay * by - az * bz,
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
  ];
}

export function slerpQuaternion(left: QuaternionWxyz, right: QuaternionWxyz, amount: number): QuaternionWxyz {
  let target = right;
  let dot = left.reduce((sum, component, index) => sum + component * target[index], 0);
  if (dot < 0) {
    target = target.map((component) => -component) as unknown as QuaternionWxyz;
    dot = -dot;
  }
  dot = Math.max(-1, Math.min(1, dot));
  if (dot > 0.9995) {
    const blended = left.map((component, index) => component + amount * (target[index] - component)) as unknown as QuaternionWxyz;
    return normalizeQuaternion(blended) || left;
  }
  const theta = Math.acos(dot);
  const sine = Math.sin(theta);
  const a = Math.sin((1 - amount) * theta) / sine;
  const b = Math.sin(amount * theta) / sine;
  return left.map((component, index) => a * component + b * target[index]) as unknown as QuaternionWxyz;
}

function sample(series: SignalNativeSeriesV1[], index: number): QuaternionWxyz | "gap" | "invalid" {
  const raw = series.map((item) => item.values[index]);
  if (raw.some((value) => value == null)) return "gap";
  return normalizeQuaternion(raw as unknown as QuaternionWxyz) || "invalid";
}

export function orientationAtTime(
  window: SignalWindowV1,
  modality: QuaternionSignalModalityV1,
  selectedTime: number,
  calibration: QuaternionWxyz = RX_NEGATIVE_90,
): OrientationSelection {
  const aliases = [modality.components.w, modality.components.x, modality.components.y, modality.components.z];
  const source = aliases.map((choices) => window.series.find((candidate) =>
    candidate.representation === "native" && candidate.modality_id === modality.id && choices.includes(candidate.channel_label),
  ));
  if (source.some((item) => !item || item.representation !== "native")) return { state: "unavailable", reason: "mapping" };
  const series = source as SignalNativeSeriesV1[];
  const start = series[0].start_seconds;
  const period = series[0].sample_period_seconds;
  if (series.some((item) => Math.abs(item.start_seconds - start) > 1e-9 || Math.abs(item.sample_period_seconds - period) > 1e-9)) {
    return { state: "unavailable", reason: "mapping" };
  }
  const last = start + (series[0].sample_count - 1) * period;
  if (selectedTime < start - 1e-9 || selectedTime > last + 1e-9) return { state: "unavailable", reason: "out_of_coverage" };
  const position = Math.max(0, Math.min(series[0].sample_count - 1, (selectedTime - start) / period));
  const lowerIndex = Math.floor(position + 1e-9);
  const upperIndex = Math.ceil(position - 1e-9);
  const lower = sample(series, lowerIndex);
  const upper = sample(series, upperIndex);
  if (typeof lower === "string" || typeof upper === "string") {
    return { state: "unavailable", reason: lower === "gap" || upper === "gap" ? "gap" : "invalid" };
  }
  const lowerTime = start + lowerIndex * period;
  const upperTime = start + upperIndex * period;
  const maximumGap = Math.min(modality.quaternion.max_gap_seconds, period * 2 + 1e-9);
  if (upperTime - lowerTime > maximumGap) return { state: "unavailable", reason: "gap" };
  const sensor = lowerIndex === upperIndex ? lower : slerpQuaternion(lower, upper, (selectedTime - lowerTime) / (upperTime - lowerTime));
  const model = normalizeQuaternion(multiplyQuaternion(calibration, sensor));
  if (!model) return { state: "unavailable", reason: "invalid" };
  return { state: "available", sensor, model, sampleTime: selectedTime };
}

export function quaternionToXyzw(value: QuaternionWxyz): readonly [number, number, number, number] {
  return [value[1], value[2], value[3], value[0]];
}

export function rotateVectorByQuaternion(value: Vector3, quaternion: QuaternionWxyz): Vector3 {
  const [w, x, y, z] = quaternion;
  const [vx, vy, vz] = value;
  const tx = 2 * (y * vz - z * vy);
  const ty = 2 * (z * vx - x * vz);
  const tz = 2 * (x * vy - y * vx);
  return [
    vx + w * tx + (y * tz - z * ty),
    vy + w * ty + (z * tx - x * tz),
    vz + w * tz + (x * ty - y * tx),
  ];
}

export function tiltOnlyQuaternion(calibrated: QuaternionWxyz): TiltOnlyOrientation {
  const normalized = normalizeQuaternion(calibrated);
  if (!normalized) throw new TypeError("calibrated orientation must be a unit quaternion");
  const inverse: QuaternionWxyz = [normalized[0], -normalized[1], -normalized[2], -normalized[3]];
  const bodyUp = rotateVectorByQuaternion([0, 1, 0], inverse);
  const dot = Math.max(-1, Math.min(1, bodyUp[1]));
  const cross: Vector3 = [-bodyUp[2], 0, bodyUp[0]];
  const crossLength = Math.hypot(...cross);
  const inclinationDegrees = Math.atan2(crossLength, dot) * 180 / Math.PI;
  if (crossLength <= 1e-10) {
    return {
      quaternion: dot < 0 ? [0, 1, 0, 0] : [1, 0, 0, 0],
      inclinationDegrees,
      singular: dot < 0,
    };
  }
  if (1 + dot < 1e-8) {
    const angle = Math.atan2(crossLength, dot);
    const sine = Math.sin(angle / 2);
    return {
      quaternion: [
        Math.cos(angle / 2),
        cross[0] / crossLength * sine,
        cross[1] / crossLength * sine,
        cross[2] / crossLength * sine,
      ],
      inclinationDegrees,
      singular: false,
    };
  }
  const raw: QuaternionWxyz = [1 + dot, cross[0], cross[1], cross[2]];
  const norm = Math.hypot(...raw);
  if (norm <= 1e-12) throw new TypeError("tilt-only quaternion could not be normalized");
  const shortest = raw.map((value) => value / norm) as unknown as QuaternionWxyz;
  return { quaternion: shortest, inclinationDegrees, singular: false };
}

export function quaternionEulerDegrees(value: QuaternionWxyz): { pitch: number; roll: number; yaw: number } {
  const [w, x, y, z] = value;
  const roll = Math.atan2(2 * (w * x + y * z), 1 - 2 * (x * x + y * y));
  const pitch = Math.asin(Math.max(-1, Math.min(1, 2 * (w * y - z * x))));
  const yaw = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
  const degrees = 180 / Math.PI;
  return { pitch: pitch * degrees, roll: roll * degrees, yaw: yaw * degrees };
}
